import { connect } from 'react-redux';
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import TableFilter from "src/components/TableFilter";
import KanbanboardService from "src/apis/KanbanboardService";
import DateUtils from "src/services/utils/DateUtils";
import { taskStatuses, priority } from 'src/pages/Reporting/TaskManager/constants';

const renderedStatuses = []
for(let i = 0; i < taskStatuses.length; i++) {
    const status = taskStatuses[i];
    renderedStatuses.push({
        id: status.value,
        title: status.label
    });
}

const renderedPriorities = []
for (let i = 0; i < priority.length; i++) {
    const pri = priority[i];
    renderedPriorities.push({
        id: pri.value,
        title: pri.baseLabel
    });
}


const FilteringComponent = ({
    t,
    modules,
    onChange
}) => {
    const [tableFilters, setTableFilters] = useState({
        fromDate: null,
        toDate: null,
        owner: null,
        manager: null,
        status: null,
        priority: null,
        sortBy: null,
        search: null
    });

    const isFirstRender = useRef(true);

    const dateUtils = new DateUtils();

    const {
        isLoading: usersListIsLoading,
        isFetching: usersListIsFetching,
        data: usersList
    } = useQuery({
        queryKey                :   [
            "app-fetch-tasks-managers-list"
        ],
        queryFn                 :   async () => {
            const service = KanbanboardService.getInstance();

            return await service.fetchManagersList({}, modules.active);
        },
        cacheTime               :   0,
        refetchOnWindowFocus    :   false,
        onError                 :   (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while fetching analyst admins list."), {
                type: "error",
            });
        }
    });

    const {
        isLoading: ownersAreLoading,
        isFetching: ownersAreFetching,
        data: ownersList
    } = useQuery({
        queryKey                :   [
            "app-fetch-tasks-owners-list"
        ],
        queryFn                 :   async () => {
            const service = KanbanboardService.getInstance();

            return await service.fetchTaskOwners({
                inactive: true
            }, modules.active);
        },
        cacheTime               :   0,
        refetchOnWindowFocus    :   false,
        onError                 :   (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while fetching owners list."), {
                type: "error",
            });
        },
    });

    const sortOptions = useMemo(() => {
        return [
            {
                id      :   'priority',
                title   :   'Priority',
            },
            {
                id      :   'deadline',
                title   :   'Deadline'
            }
        ]
    }, []);

    const tableFilterTabs = useMemo(() => {
        return [
            {
                type: 'date',
                name: 'deadline-date',
                title: 'Deadline',
                icon: <i className="ri-calendar-event-line" />,
                defaultValue: {
                    from: tableFilters['fromDate'],
                    to: tableFilters['toDate']
                }
            },
            {
                type: 'radio_list',
                name: 'sortBy',
                title: 'Sort By',
                icon: <i className="ri-sort-asc" />,
                defaultValue: tableFilters?.sortBy,
                data: sortOptions,
                options: {
                    disableAll: true
                },
                formatter: (item) => {
                    return t(item.title)
                }
            },
            {
                type: 'radio_list',
                name: 'owner',
                title: 'Task Owner',
                icon: <i className="ri-user-line" />,
                isLoading: ownersAreFetching || ownersAreLoading,
                defaultValue: tableFilters?.owner,
                data: ownersList || [],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return `${item.first_name} ${item.last_name}`
                }
            },
            {
                type: 'radio_list',
                name: 'manager',
                title: 'Task Manager',
                icon: <i className="ri-user-settings-fill" />,
                isLoading: usersListIsFetching || usersListIsLoading,
                defaultValue: tableFilters?.manager,
                data: usersList || [],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return `${item.first_name} ${item.last_name}`
                }
            },
            {
                type: 'radio_list',
                name: 'status',
                title: 'Status',
                isLoading: false,
                icon: <i className="ri-bar-chart-line" />,
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                defaultValue: tableFilters?.status,
                data: renderedStatuses,
                formatter: (item) => {
                    return item.title
                }
            },
            {
                type: 'radio_list',
                name: 'priority',
                title: 'Priority',
                icon: <i className="ri-list-check-2" />,
                isLoading: false,
                defaultValue: tableFilters?.priority,
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                data: renderedPriorities,
                formatter: (item) => {
                    return item.title
                }
            },
            {
                type: 'search_textbox',
                name: 'search',
                title: t('Search by title or description'),
                icon: <i className="ri-terminal-box-line" />,
                defaultValue: tableFilters?.search,
                options: {
                    placeholder: t('Search by title or description'),
                    typingDelay: 750
                },
                displayIndependently: true,
                onFilterChange: (value) => {
                    setTableFilters((currentState) => ({...currentState, search: value}))
                }
            }
        ]
    }, [
        tableFilters,
        usersListIsLoading,
        usersListIsFetching,
        ownersAreLoading,
        ownersAreFetching,
        ownersList,
        usersList,
        sortOptions,
        t
    ]);

    const onTableFilterOkButtonClicked = (filters, independentTabsData) => {
        if (filters) {
            const result = {}

            if ('deadline-date' in filters) {
                result['fromDate'] = dateUtils.convertDateToDate(
                    new Date(filters['deadline-date']?.from),
                    'YYYY-MM-DD'
                )

                result['toDate'] = dateUtils.convertDateToDate(
                    new Date(filters['deadline-date']?.to),
                    'YYYY-MM-DD'
                )
            }

            if ('sortBy' in filters) {
                result.sortBy = filters.sortBy || undefined
            }

            if ('owner' in filters) {
                result.owner = filters.owner !== 'all' ? filters.owner : undefined
            }

            if ('manager' in filters) {
                result.manager = filters.manager !== 'all' ? filters.manager : undefined
            }

            if ('status' in filters) {
                result.status =
                    filters.status !== 'all' ? filters.status : undefined
            }

            if ('priority' in filters) {
                result.priority =
                    filters.priority !== 'all' ? filters.priority : undefined
            }
            
            setTableFilters({
                ...tableFilters,
                ...result
            })
        } else {
            setTableFilters({
                fromDate: null,
                toDate: null,
                owner: null,
                manager: null,
                status: null,
                priority: null,
                sortBy: null,
                search: independentTabsData?.search
            })
        }
    }

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        onChange && onChange(tableFilters)
    }, [ 
        tableFilters,
        onChange
    ]);

    return (
        <TableFilter
            className="me-2"
            defaultActiveTab='deadline-date'
            tabs={tableFilterTabs}
            onOkButtonClicked={
                onTableFilterOkButtonClicked
            }
        />
    )
}

const mapStatetoProps = (state) => {
    const { 
		Modules: modules 
	} = state;

	return {
        modules
	};
};

export default withNamespaces()(
    connect(
        mapStatetoProps
    )(FilteringComponent)
);